import { FC, useEffect, useMemo, useState } from 'react';

import { Link, useLocation } from 'react-router-dom';

import {
  setAdminPageTitle,
  useAdminPageTitle
} from '@/shared/lib/adminPageTitle';

import {
  ADMIN_ROUTE_META,
  ADMIN_ROUTES,
  type IAdminRouteMeta
} from '@/shared/constants';

import { useAdminUser } from '@/entities/admin-session';

import { useAdminLogout } from '@/features/admin-auth';

import { getAdminIcon } from './icons';

interface IProps {
  onMenuClick: () => void;
}

/**
 * Возвращает имя администратора для topbar.
 * @param adminUser Данные администратора
 */
const getAdminName = (adminUser: ReturnType<typeof useAdminUser>) => {
  if (!adminUser) return 'Администратор';

  const shortName = [adminUser?.first_name, adminUser?.last_name]
    .filter(Boolean)
    .join(' ');
  const candidates = [
    adminUser.full_name,
    shortName,
    adminUser.email,
    adminUser.phone
  ];
  const candidate = candidates.find(value => Boolean(value));

  return candidate || 'Администратор';
};

/**
 * Возвращает title текущего admin route.
 * @param pathname Текущий путь
 */
const getRouteTitle = (pathname: string) => {
  if (pathname === ADMIN_ROUTES.castings.new) return 'Создание кастинга';
  if (pathname === `${ADMIN_ROUTES.users.index}/new`) {
    return 'Создание пользователя';
  }
  if (pathname.endsWith('/new')) return 'Создание записи';

  if (/^\/admin-v2\/castings\/[^/]+\/edit$/.test(pathname)) {
    return 'Редактирование кастинга';
  }

  if (/^\/admin-v2\/.+\/[^/]+\/edit$/.test(pathname)) {
    return 'Редактирование';
  }

  const exact = ADMIN_ROUTE_META.find(meta => meta.to === pathname);
  if (exact) return exact.title;

  const dynamic = ADMIN_ROUTE_META.find(meta => {
    if (!meta.to.includes(':id')) return false;

    const pattern = new RegExp(`^${meta.to.replace(':id', '[^/]+')}$`);
    return pattern.test(pathname);
  });

  return dynamic?.title || 'Админ';
};

/**
 * Возвращает родительский раздел (index ресурса) для create/edit/detail-страницы,
 * чтобы крошка вела назад в список текущего раздела, а не в захардкоженный
 * `/admin-v2/users`. Для самой index-страницы возвращает null (родителя нет).
 * @param pathname Текущий путь
 */
const getAdminSection = (pathname: string): IAdminRouteMeta | null => {
  let sectionPath: string | null = null;

  if (pathname.endsWith('/new')) {
    sectionPath = pathname.slice(0, -'/new'.length);
  } else {
    const editMatch = pathname.match(/^(.*)\/[^/]+\/edit$/);
    const detailMatch = pathname.match(/^(.*)\/[^/]+$/);

    if (editMatch) sectionPath = editMatch[1];
    else if (detailMatch) sectionPath = detailMatch[1];
  }

  if (!sectionPath) return null;

  return ADMIN_ROUTE_META.find(meta => meta.to === sectionPath) || null;
};

/**
 * Проверяет stale ActiveStorage disk URL из старых seed/fixture attachments.
 * @param url URL аватара
 */
const isKnownBrokenAvatarUrl = (url: string) => {
  const normalized = url.toLowerCase();

  return (
    normalized.includes('/rails/active_storage/disk/') &&
    normalized.endsWith('/sample.jpg')
  );
};

/**
 * Возвращает URL аватара только из backend-данных.
 * @param adminUser Данные администратора
 */
const getAdminAvatarUrl = (adminUser: ReturnType<typeof useAdminUser>) => {
  if (!adminUser) return null;

  const record = adminUser as unknown as Record<string, unknown>;
  const value = [
    record.photo,
    record.photo_url,
    record.avatar_url,
    record.image_url,
    record.system_photo
  ].find(item => typeof item === 'string' && item);

  if (typeof value !== 'string' || isKnownBrokenAvatarUrl(value)) return null;

  return value;
};

/**
 * Topbar админки.
 * @param root0 Пропсы
 * @param root0.onMenuClick Открытие мобильного меню
 */
export const AdminTopbar: FC<IProps> = ({ onMenuClick }) => {
  const { pathname } = useLocation();
  const adminUser = useAdminUser();
  const logout = useAdminLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const adminName = getAdminName(adminUser);
  const adminAvatarUrl = getAdminAvatarUrl(adminUser);
  const pageTitleOverride = useAdminPageTitle();
  const staticTitle = useMemo(() => getRouteTitle(pathname), [pathname]);
  // Переопределение показываем только на своём pathname (см. adminPageTitle.ts)
  // — так заголовок предыдущей карточки не может пережить смену маршрута
  // независимо от порядка эффектов топбара и страницы (19058, волна 3, F9).
  const currentTitle =
    pageTitleOverride?.pathname === pathname
      ? pageTitleOverride.title
      : staticTitle;
  const section = useMemo(() => getAdminSection(pathname), [pathname]);

  // Доп. подстраховка: сброс на смену маршрута, чтобы стор не носил чужой
  // pathname дольше, чем нужно (сам топбар от утечки уже защищён проверкой выше).
  useEffect(() => {
    setAdminPageTitle(null, pathname);
  }, [pathname]);
  const MenuIcon = getAdminIcon('menu');
  const ChevronIcon = getAdminIcon('chevron-down');
  const UserIcon = getAdminIcon('user');
  const SettingsIcon = getAdminIcon('settings');
  const LogoutIcon = getAdminIcon('log-out');

  return (
    <header className='topbar'>
      <button
        className='topbar__mobile-toggle'
        type='button'
        aria-label='Открыть меню'
        onClick={onMenuClick}
      >
        <MenuIcon />
      </button>
      <div className='topbar__crumbs'>
        {section ? (
          <>
            <Link to={section.to}>{section.title}</Link>
            <span className='topbar__crumbs-sep'>/</span>
          </>
        ) : null}
        <span className='topbar__crumbs-current'>{currentTitle}</span>
      </div>
      <div className={`dropdown${menuOpen ? ' open' : ''}`}>
        <button
          className='topbar__user'
          type='button'
          aria-label={`Меню администратора: ${adminName}`}
          onClick={() => setMenuOpen(value => !value)}
        >
          {adminAvatarUrl ? (
            <span className='topbar__user-avatar'>
              <img src={adminAvatarUrl} alt='' width={32} height={32} />
            </span>
          ) : (
            <span className='topbar__user-avatar'>
              <UserIcon style={{ width: 18 }} />
            </span>
          )}
          <ChevronIcon style={{ opacity: 0.6, width: 14 }} />
        </button>
        <div className={`topbar__user-menu${menuOpen ? ' open' : ''}`}>
          <div className='topbar__user-menu-current'>{adminName}</div>
          <Link to={ADMIN_ROUTES.users.show('1')}>
            <UserIcon style={{ width: 16 }} />
            Профиль
          </Link>
          <Link to={ADMIN_ROUTES.settings}>
            <SettingsIcon style={{ width: 16 }} />
            Настройки
          </Link>
          <button
            className='danger'
            type='button'
            onClick={() => {
              setMenuOpen(false);
              logout();
            }}
          >
            <LogoutIcon style={{ width: 16 }} />
            Выход
          </button>
        </div>
      </div>
    </header>
  );
};
